// src/components/admin/analytics/TrafficTab.jsx
import {
  FaGlobe,
  FaUsers,
  FaEye,
  FaChartBar,
  FaCog,
  FaExternalLinkAlt,
  FaMobileAlt,
  FaDesktop,
  FaTabletAlt,
} from 'react-icons/fa';
import { MdRefresh } from 'react-icons/md';
import useTrafficAnalytics from '../../../hooks/useTrafficAnalytics';

const MetricCard = ({ title, value, sub, icon: Icon, color, bgColor }) => (
  <div className="bg-white rounded-lg shadow p-6">
    <div className="flex items-center justify-between mb-4">
      <div className={`${bgColor} p-3 rounded-full`}>
        <Icon className={`w-6 h-6 ${color}`} />
      </div>
    </div>
    <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
    <p className="text-2xl font-bold text-gray-900">{value}</p>
    {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
  </div>
);

const DEVICE_ICONS = {
  desktop: FaDesktop,
  mobile: FaMobileAlt,
  tablet: FaTabletAlt,
};

const SOURCE_COLORS = [
  'bg-blue-500',
  'bg-green-500',
  'bg-purple-500',
  'bg-orange-500',
  'bg-pink-500',
  'bg-teal-500',
];

const SetupGuide = () => (
  <div className="space-y-6">
    <div className="bg-amber-50 border border-amber-200 rounded-lg p-5">
      <div className="flex items-start gap-3">
        <FaCog className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-sm font-semibold text-amber-800">GA4 Integration Setup Required</p>
          <p className="text-sm text-amber-700 mt-1">
            Traffic data will appear here once the GA4 proxy function is deployed and configured.
            Follow the steps below to get set up.
          </p>
        </div>
      </div>
    </div>

    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-base font-semibold text-gray-900 mb-4">Setup Steps</h3>
      <ol className="space-y-4">
        {[
          {
            step: '1',
            title: 'Deploy the analytics proxy function',
            desc: 'Add analyticsProxy.js to your functions directory and run firebase deploy --only functions.',
          },
          {
            step: '2',
            title: 'Install the GA4 Data API package',
            desc: 'In your functions directory: npm install @google-analytics/data',
          },
          {
            step: '3',
            title: 'Find your GA4 Property ID',
            desc: 'In Google Analytics → Admin → Property Settings. It\'s a numeric ID like "123456789". Paste it into analyticsProxy.js where it says YOUR_GA4_PROPERTY_ID.',
          },
          {
            step: '4',
            title: 'Grant your Firebase service account GA4 access',
            desc: 'In GA4 → Admin → Property Access Management, add your Firebase service account email (found in GCP → IAM) as a Viewer.',
          },
          {
            step: '5',
            title: 'Enable the Google Analytics Data API',
            desc: 'In GCP Console → APIs & Services → Library, search for "Google Analytics Data API" and enable it for your project.',
          },
        ].map(({ step, title, desc }) => (
          <li key={step} className="flex gap-4">
            <span className="flex-shrink-0 w-7 h-7 bg-green-600 text-white rounded-full flex items-center justify-center text-xs font-bold">
              {step}
            </span>
            <div>
              <p className="text-sm font-semibold text-gray-800">{title}</p>
              <p className="text-sm text-gray-600 mt-0.5">{desc}</p>
            </div>
          </li>
        ))}
      </ol>
    </div>

    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-base font-semibold text-gray-900 mb-3">What you'll see once connected</h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {['Sessions', 'Unique Users', 'Page Views', 'Bounce Rate', 'Traffic Sources', 'Top Pages', 'Device Breakdown', 'Avg. Session Duration'].map((item) => (
          <div key={item} className="bg-gray-50 rounded-md px-3 py-2 text-xs text-gray-600 font-medium">
            {item}
          </div>
        ))}
      </div>
    </div>

    <div className="flex gap-3">
      <a
        href="https://analytics.google.com"
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
      >
        Open Google Analytics <FaExternalLinkAlt className="w-3 h-3" />
      </a>
      <a
        href="https://console.cloud.google.com"
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors"
      >
        Open GCP Console <FaExternalLinkAlt className="w-3 h-3" />
      </a>
    </div>
  </div>
);

const TrafficTab = ({ dateRange }) => {
  const { data, loading, error, configured, refresh } = useTrafficAnalytics(dateRange);

  if (!configured) {
    return <SetupGuide />;
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-white rounded-lg shadow p-6 animate-pulse">
              <div className="w-12 h-12 bg-gray-200 rounded-full mb-4" />
              <div className="h-3 bg-gray-200 rounded w-3/4 mb-2" />
              <div className="h-7 bg-gray-200 rounded w-1/2" />
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow p-6 animate-pulse h-64" />
          <div className="bg-white rounded-lg shadow p-6 animate-pulse h-64" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-5 flex items-center justify-between">
        <p className="text-sm text-red-700">{error}</p>
        <button
          onClick={refresh}
          className="flex items-center gap-2 px-3 py-1.5 bg-red-100 text-red-700 text-sm rounded-lg hover:bg-red-200"
        >
          <MdRefresh className="w-4 h-4" /> Retry
        </button>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="space-y-6">
      {/* KPI Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Sessions"
          value={data.sessions.toLocaleString()}
          sub={`${dateRange === '30d' ? '30-day' : dateRange} window`}
          icon={FaGlobe}
          color="text-blue-600"
          bgColor="bg-blue-100"
        />
        <MetricCard
          title="Unique Users"
          value={data.users.toLocaleString()}
          sub="distinct visitors"
          icon={FaUsers}
          color="text-green-600"
          bgColor="bg-green-100"
        />
        <MetricCard
          title="Page Views"
          value={data.pageViews.toLocaleString()}
          sub={`${data.sessions > 0 ? (data.pageViews / data.sessions).toFixed(1) : '0'} pages/session`}
          icon={FaEye}
          color="text-purple-600"
          bgColor="bg-purple-100"
        />
        <MetricCard
          title="Bounce Rate"
          value={`${data.bounceRate}%`}
          sub={`${data.avgSessionDuration} avg session`}
          icon={FaChartBar}
          color="text-orange-600"
          bgColor="bg-orange-100"
        />
      </div>

      {/* Traffic Sources + Top Pages */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Traffic Sources */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-base font-semibold text-gray-900 mb-4">Traffic Sources</h3>
          {!data.trafficSources?.length ? (
            <p className="text-sm text-gray-400 text-center py-8">No source data available</p>
          ) : (
            <div className="space-y-3">
              {data.trafficSources.map((source, idx) => (
                <div key={source.source}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="font-medium text-gray-700 capitalize">
                      {source.source.replace(/_/g, ' ')}
                    </span>
                    <span className="text-gray-500">
                      {source.sessions.toLocaleString()} sessions ({source.percentage}%)
                    </span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${SOURCE_COLORS[idx % SOURCE_COLORS.length]}`}
                      style={{ width: `${source.percentage}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Top Pages */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-base font-semibold text-gray-900 mb-4">Top Pages</h3>
          {!data.topPages?.length ? (
            <p className="text-sm text-gray-400 text-center py-8">No page data available</p>
          ) : (
            <div className="space-y-3">
              {data.topPages.map((page) => (
                <div key={page.path} className="flex items-center justify-between">
                  <span
                    className="text-sm text-gray-700 font-mono truncate max-w-[60%]"
                    title={page.path}
                  >
                    {page.path}
                  </span>
                  <div className="text-right">
                    <span className="text-sm font-semibold text-gray-900">
                      {page.views.toLocaleString()}
                    </span>
                    <span className="text-xs text-gray-400 ml-1">views</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Device Breakdown */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-base font-semibold text-gray-900 mb-4">Device Types</h3>
        {!data.devices?.length ? (
          <p className="text-sm text-gray-400 text-center py-4">No device data available</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {data.devices.map((device) => {
              const key = device.device.toLowerCase();
              const Icon = DEVICE_ICONS[key] || FaDesktop;
              return (
                <div
                  key={device.device}
                  className="flex items-center gap-4 bg-gray-50 rounded-lg p-4"
                >
                  <Icon className="w-6 h-6 text-gray-500 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-gray-900 capitalize">
                      {device.device}
                    </p>
                    <p className="text-xs text-gray-500">
                      {device.sessions.toLocaleString()} sessions ({device.percentage}%)
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default TrafficTab;